import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { observer } from 'mobx-react-lite'
import moment from 'moment'
import { debounce } from 'lodash'
import classNames from 'classnames'
import * as qs from 'qs'

// evertel
import {
    TanTable, TanTableColumnDef,
    PageBody, Badge, Text, PhotoGallery,
    Card, CardBody, CardHeader, useUI,
    Spinner
} from '@evertel/web/ui'
import { DocumentSearchForm } from './DocumentSearchForm'
import { useService } from '@evertel/di'
import { DocumentSearchController, DocumentSearchFilter, DocumentSchemaStore } from '@evertel/document'
import { DocumentIcon, DocumentModal } from '../elements'
import { DocumentShareModal } from '..'
import { SessionService } from '@evertel/session/SessionService'
import { useDidMountEffect } from '@evertel/hooks'
import { APIDataBlueUser, APIDataDocument, APIDataDocumentACL } from '@evertel/types'

const DocumentSearch: React.FC = observer(() => {

    const navigate = useNavigate()
    const location = useLocation()
    const [isLoading, setIsLoading] = useState(true)
    // grab filter params from query string
    const { toggleLeftNav } = useUI()

    const [searchFilter, setSearchFilter] = useState<DocumentSearchFilter>({by: null, state: 'published', text: ''})
    const [showShareModal, setShowShareModal] = useState(false)
    const [selectedDoc, setSelectedDoc] = useState({
        document: null,
        show: false
    })

    const documentSearchController = useService(DocumentSearchController, [])
    const documentSchemaStore = useService(DocumentSchemaStore, [])
    const sessionService = useService(SessionService, [])

    useEffect(() => {
        // react gives useState render errors if this is inside useState
        toggleLeftNav('open')
    }, [])

    // parse query string
    useState(() => {
        documentSearchController.init({ isInfiniteScroll: false })

        // load all parameters from URL
        // parameters need to be processed before <DocumentSearchForm> is loaded
        const parsedQuery = qs.parse(location.search, { ignoreQueryPrefix: true })

        if (typeof parsedQuery.limit === 'string') {
            documentSearchController.setLimit(parseInt(parsedQuery.limit))
        }
        if (typeof parsedQuery.order === 'string') {
            documentSearchController.setOrder(parsedQuery.order)
        }
        if (typeof parsedQuery.filter === 'string') {
            try {
                setSearchFilter(JSON.parse(decodeURIComponent(parsedQuery.filter)))
            } catch { /* nothing */ }
        }
        //must be set last as all the others reset the page
        if (typeof parsedQuery.page === 'string') {
            documentSearchController.setPage(parseInt(parsedQuery.page), true)
        }

        //restore a document that was being viewed
        if (typeof parsedQuery.selectedDoc === 'string') {
            const docId = parsedQuery.selectedDoc
            //could attempt to load document here, but the view modal should take care of that
            //the share modal will be missing the doc name, but that's not significant
            setSelectedDoc({
                document: { id: docId },
                show: true
            })

        }

    })

    const updateQueryString = (params: any) => {
        const currentQuery = qs.parse(location.search, { ignoreQueryPrefix: true })
        const newQuery = { ...currentQuery, ...params }
        const searchParams = qs.stringify(newQuery)
        navigate(`/everdocs?${searchParams}`, { replace: true })
    }

    useEffect(() => {
        if (!documentSearchController.isLoading) {
            setIsLoading(false)
        }
    }, [documentSearchController.isLoading])


    useEffect(() => {
        // initialize (fetch available schemas and departments for search filters and table display)
        documentSearchController.fetchDocumentSchemas()

    }, [sessionService.currentUserId, sessionService.selectedDepartmentId])

    useEffect(() => {
        // SEARCH (triggered each time the searchFilter changes)
        documentSearchController.setFilter(searchFilter)
        search()
    }, [searchFilter])

    useDidMountEffect(() => {
        // SEARCH (triggered each time the page/limit/order changes.)
        search()
    }, [documentSearchController.page, documentSearchController.limit, documentSearchController.order])

    const debouncedSearch = useCallback(debounce(async () => {
        setIsLoading(true)

        // update query string with each search, stripping null/empty values for a clean URL
        const cleanFilter = Object.fromEntries(
            Object.entries(documentSearchController.filter).filter(([_, v]) => v != null && v !== '')
        )
        updateQueryString({
            filter: JSON.stringify(cleanFilter),
            page: documentSearchController.page,
            limit: documentSearchController.limit,
            order: documentSearchController.order
        })

        try {
            await documentSearchController.search()
        } catch (error) {
            console.error(error.message)
        }
        //this dependency list must be empty or debounce will be created over and over and not work
    }, 500), [])

    const search = useCallback(() => {
        //setting loading to true sooner feels more responsive
        setIsLoading(true)
        debouncedSearch()
    }, [debouncedSearch])


    const filteredSchemas = useMemo(() => {
        if (searchFilter.by?.type === 'department') {
            return documentSearchController.documentSchemas.filter(
                s => s.departmentId === searchFilter.by.id
            )
        }
        return documentSearchController.documentSchemas
    }, [documentSearchController.documentSchemas, searchFilter.by])

    const onFilterChange = (filter) => {
        setSearchFilter((prev) => ({
            ...prev,
            ...filter
        }))
    }

    const onEditDoc = (documentId) => {
        //pass along current location to determine if clicking back is ok from the edit doc view
        navigate(`/everdocs/edit/${documentId}`, {
            state: {
                from: location.pathname + location.search
            }
        })
    }

    const handleDocumentSelect = (row: any) => {
        const document = row.original as APIDataDocument
        setSelectedDoc({
            document: document,
            show: true
        })
        // Add selectedDoc to URL
        updateQueryString({ selectedDoc: document.id })
    }

    const handleDocumentClose = () => {
        setSelectedDoc((prev) => ({
            ...prev,
            show: false
        }))
        // Remove selectedDoc from URL using the helper
        updateQueryString({ selectedDoc: undefined })
    }

    const columns = useMemo<TanTableColumnDef<APIDataDocument>[]>(() => [
        {
            header: 'Name/Title',
            accessorKey: 'name',
            size: '30%',
            enableSorting: true,
            cell: ({ row }) => (
                <div className="d-flex">
                    <DocumentIcon
                        className="me-2"
                        schema={documentSchemaStore.findById(row.original.documentSchemaId)}
                    />
                    <strong className={classNames({ 'text-muted': !row.original.name })}>
                        {row.original.name || 'Untitled Document'}
                    </strong>
                    {row.original.isArchived && (
                        <Badge
                            className="ms-2"
                            color="muted">
                            Archived
                        </Badge>
                    )}
                </div>
            )
        },
        {
            header: 'Type',
            accessorKey: 'documentSchemaId',
            size: '15%',
            enableSorting: true,
            cell: ({ row }) => (
                <Text size={13}>
                    {documentSchemaStore.findById(row.original.documentSchemaId)?.name}
                </Text>
            )
        },
        {
            header: 'Last Modified',
            size: '20%',
            accessorKey: 'updatedDate',
            accessorFn: (row) => moment(row.updatedDate).format('ll HH:mm'),
            enableSorting: true,
            sortDescFirst: true,
            cell: ({ row, getValue }) => (
                <Text
                    size={13}
                >
                    {getValue() as string}
                </Text>
            )
        },
        {
            id: 'acl',
            header: 'Owner',
            size: '20%',
            accessorFn: (row) => {
                const ownerAcl = row.ACL?.find(a => a.accessType === 'OWNER')
                if (!ownerAcl) return ''

                const owner = ownerAcl?.principal as APIDataBlueUser
                return (owner?.firstName?.trim() || owner?.lastName?.trim())
                    ? `${owner?.firstName || ''} ${owner?.lastName || ''}`.trim()
                    : (owner?.email || owner?.id || '??????????')
            },
            enableSorting: false
        },
        {
            id: 'agency',
            header: 'Agency',
            size: '20%',
            accessorFn: (row) => {
                return `${row?.department?.name}`
            },
            enableSorting: false,
            cell: ({ cell, getValue }) => (
                <Text size={13}>
                    {cell.getValue() as string}
                </Text>
            )
        }
    ], [documentSearchController.documentSchemaIds])

    return (
        <PageBody>
            <Card style={{ minHeight: '90vh' }}>
                <CardHeader>
                    <Text
                        tag="h3"
                        className="float-left"
                    >
                        EverDocs
                    </Text>
                    <div aria-live="polite">
                        {(!isLoading) ? (
                            <Badge
                                color="muted"
                                className="ms-2"
                            >
                                {documentSearchController.documentsCount}
                                <span className="visually-hidden">{`${documentSearchController.documentsCount === 1 ? 'document' : 'documents'}`} found</span>
                            </Badge>
                        ) : (
                            <div className="d-flex align-items-center justify-content-center" style={{height: 0, overflow: 'visible'}}>
                                <Spinner width={40}/>
                                <span className="visually-hidden">Loading results</span>
                            </div>
                        )}
                    </div>
                </CardHeader>
                <CardBody>
                    <DocumentSearchForm
                        filter={searchFilter}
                        onFilterChange={onFilterChange}
                        documentDepartments={documentSearchController.documentDepartments}
                        documentSchemas={filteredSchemas}
                    />
                    <TanTable
                        className="table-hover"
                        remote={true}
                        loading={isLoading}
                        data={documentSearchController.documents}
                        rowCount={documentSearchController.documentsCount}
                        columns={columns}
                        options={{
                            responsive: true,
                            responsiveHeight: 'calc(100vh - 280px)',
                            truncateCells: true,
                            pageSize: documentSearchController.limit,
                            pageNumber: documentSearchController.page,
                            sortName: documentSearchController.order ? documentSearchController.order.split(' ')[0] : null,
                            sortOrder: documentSearchController.order ? documentSearchController.order.split(' ')[1] as 'asc' | 'desc' : null,
                            onPageChange: (page) => documentSearchController.setPage(page),
                            onPageSizeChange: (pageSize) => documentSearchController.setLimit(pageSize),
                            onSortChange: (sortName, sortOrder) => {
                                const newOrder = (sortOrder) ? `${sortName} ${sortOrder}` : null
                                documentSearchController.setOrder(newOrder)
                            }
                        }}
                        onRowClick={handleDocumentSelect}
                        aria-label="Documents list"
                    />
                </CardBody>
            </Card>

            <PhotoGallery>
                <DocumentModal
                    visible={selectedDoc?.show}
                    documentId={selectedDoc?.document?.id}
                    onClose={handleDocumentClose}
                    onEdit={(id) => onEditDoc(id)}
                    onShare={() => setShowShareModal(true)}
                />
            </PhotoGallery>

            <DocumentShareModal
                isVisible={showShareModal}
                backdrop={false}
                onClose={() => setShowShareModal(false)}
                documentId={selectedDoc?.document?.id}
                currentUserId={sessionService.currentUserId}
                documentName={selectedDoc?.document?.name}
            />
        </PageBody>
    )
})

export {
    DocumentSearch
}
